import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { FileTextOutlined } from '@ant-design/icons';
import { Checkbox, Drawer, Space, Typography } from 'antd';
import Button from 'components/new-ui/base/button/Button';
import HeaderWithIcon from 'components/new-ui/composed/header/HeaderWithIcon';
import DeclarationInput from 'components/ui/composed/declarations/formInput';
import Notification from 'components/ui/base/notification/Notification';
import { editCustomer } from 'store/customers/client';
import { Customer } from 'store/customers/customer';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import ThirdPartyBilling from 'views/customers/components/create-customer-drawer/ThirdPartyBilling';
import ContactDetails from './ContactDetails';
import { getValidationSchema } from './validationSchema';

type FormValues = {
    billingRecipientName: string | null;
    billingEmail: string | null;
    thirdPartyBillingId: string | null;
    billingCustomerCode: string | null;
};

type Props = {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    customer: Customer | null;
    isThirdPartyBillingRequired: boolean;
    contactDetails: {
        name: string;
        email: string;
    };
};

const BillingDetailsDrawer = ({
    open,
    onSuccess,
    onClose,
    customer,
    isThirdPartyBillingRequired,
    contactDetails,
}: Props) => {
    const [checkedBillingDetails, setCheckedBillingDetails] = useState(false);

    const validationSchema = useMemo(
        () => getValidationSchema(isThirdPartyBillingRequired),
        [isThirdPartyBillingRequired]
    );

    const formik = useFormik<FormValues>({
        initialValues: {
            billingRecipientName: customer?.customerDetails?.billingName ?? null,
            billingEmail: customer?.customerDetails?.billingEmail ?? null,
            thirdPartyBillingId: customer?.customerDetails?.thirdParty ?? null,
            billingCustomerCode: customer?.customerDetails?.billingCustomerCode ?? null,
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            if (!customer) return;

            try {
                const updatedCustomer: Customer = {
                    ...customer,
                    customerDetails: {
                        ...customer.customerDetails,
                        billingName: values.billingRecipientName ?? undefined,
                        billingEmail: values.billingEmail ?? undefined,
                        thirdParty: values.thirdPartyBillingId ?? undefined,
                        billingCustomerCode: values.billingCustomerCode ?? undefined,
                    },
                };

                await editCustomer(customer.id, updatedCustomer);

                Notification({
                    type: 'success',
                    messageTitle: 'Billing Details Updated',
                    description: `Successfully updated the billing details for ${customer.name}.`,
                });

                onSuccess();
            } catch (error) {
                Notification({
                    type: 'error',
                    messageTitle: 'Update Failed',
                    description: getErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE,
                });
            }
        },
    });

    const { setFieldValue, resetForm } = formik;

    // Keep the billing recipient in sync with the contact details while the checkbox is checked
    useEffect(() => {
        if (!checkedBillingDetails) return;
        setFieldValue('billingRecipientName', contactDetails.name);
        setFieldValue('billingEmail', contactDetails.email);
    }, [checkedBillingDetails, contactDetails.name, contactDetails.email, setFieldValue]);

    // Drop anything typed in a previous session when the drawer is closed
    useEffect(() => {
        if (open) return;
        setCheckedBillingDetails(false);
        resetForm();
    }, [open, resetForm]);

    return (
        <Drawer
            title="Billing Details"
            width={500}
            open={open}
            onClose={onClose}
            extra={
                <Space>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        type="primary"
                        onClick={formik.submitForm}
                        disabled={!formik.dirty || !customer}
                        loading={formik.isSubmitting}
                    >
                        Save
                    </Button>
                </Space>
            }
        >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <HeaderWithIcon
                    title="Billing Details"
                    subtitle={
                        <Typography.Text>
                            Default invoice recipient for this party - can be updated at any time
                        </Typography.Text>
                    }
                    iconProps={{ icon: <FileTextOutlined />, type: 'accent' }}
                    action={<div></div>}
                    style={{ marginTop: 16 }}
                />
                <Checkbox checked={checkedBillingDetails} onChange={(e) => setCheckedBillingDetails(e.target.checked)}>
                    Same as contact details
                </Checkbox>
                <ContactDetails name={contactDetails.name} email={contactDetails.email} />

                <DeclarationInput
                    required
                    condensed
                    labelSpacing="medium"
                    label="Billing Recipient Name"
                    fieldMeta={formik.getFieldMeta('billingRecipientName')}
                    fieldProps={formik.getFieldProps('billingRecipientName')}
                    fieldHelper={formik.getFieldHelpers('billingRecipientName')}
                    disabled={checkedBillingDetails}
                />

                <DeclarationInput
                    required
                    condensed
                    labelSpacing="medium"
                    label="Billing Email"
                    fieldMeta={formik.getFieldMeta('billingEmail')}
                    fieldProps={formik.getFieldProps('billingEmail')}
                    fieldHelper={formik.getFieldHelpers('billingEmail')}
                    disabled={checkedBillingDetails}
                />

                <DeclarationInput
                    required
                    condensed
                    labelSpacing="medium"
                    label="Billing Customer Code"
                    fieldMeta={formik.getFieldMeta('billingCustomerCode')}
                    fieldProps={formik.getFieldProps('billingCustomerCode')}
                    fieldHelper={formik.getFieldHelpers('billingCustomerCode')}
                />

                <ThirdPartyBilling
                    editingPartyId={customer?.id}
                    thirdPartyId={formik.values.thirdPartyBillingId ?? undefined}
                    required={isThirdPartyBillingRequired}
                    fieldMeta={formik.getFieldMeta('thirdPartyBillingId')}
                    fieldHelper={formik.getFieldHelpers('thirdPartyBillingId')}
                    onChange={(id) => setFieldValue('thirdPartyBillingId', id)}
                    onClear={() => setFieldValue('thirdPartyBillingId', undefined)}
                />
            </Space>
        </Drawer>
    );
};

export default BillingDetailsDrawer;
